// MUST be first — sets globalThis.__PARSE_NETWORK__ before any module that imports @parse/shared's
// cctp.ts evaluates, so it resolves the correct network in the dev server (see network-init.ts).
import './config/network-init'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { WagmiProvider } from 'wagmi'
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react'
import type { Adapter } from '@solana/wallet-adapter-base'
import { BrowserRouter } from 'react-router-dom'
import { ThemeProvider } from './contexts/ThemeContext'
import { TronWalletProvider } from './providers/TronWalletProvider'
import { wagmiConfig } from './config/wagmi'
import { emitWalletEvent, categorizeWalletError } from './utils/walletTelemetry'
import { WalletConnectSolanaAdapter } from './wallet/WalletConnectSolanaAdapter'
import App from './App'
import './styles/globals.css'

const queryClient = new QueryClient()

// Network-aware fallback: never silently fall back to DEVNET when running in mainnet mode —
// a devnet connection makes Jupiter/mainnet swaps fail with "address table account doesn't exist"
// and stale-blockhash reverts. If VITE_SOLANA_RPC_URL is unset, pick the cluster matching VITE_NETWORK.
const solanaRpcUrl = import.meta.env.VITE_SOLANA_RPC_URL
  || (import.meta.env.VITE_NETWORK === 'mainnet'
    ? 'https://api.mainnet-beta.solana.com'
    : 'https://api.devnet.solana.com')

// Rely on the Wallet Standard for Solana wallet discovery — modern Phantom (and Solflare/Backpack) register
// themselves, and @solana/wallet-adapter-react auto-detects them. Passing the LEGACY `new PhantomWalletAdapter()`
// on top double-registers Phantom and makes the Solana connect target the legacy `window.solana` path, which
// conflicts once Phantom-EVM (wagmi `injected`) has claimed the provider → Solana connect fails after EVM connects.
//
// The custom WalletConnect Solana adapter (#57) is the ONE exception we register here: it uses a SEPARATE WC
// relay transport (not `window.solana`), so it does not re-trigger that provider conflict. It's gated on
// VITE_WALLET_CONNECT_PROJECT_ID (same var wagmi/Tron use) — without a project id WC can't pair, so we omit it
// entirely rather than register a non-functional row. Its CAIP-2 chain is derived from VITE_NETWORK, matching
// the `solanaRpcUrl` cluster selected above (mainnet-beta vs devnet).
const wcProjectId = import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID as string | undefined
const solanaWallets: Adapter[] = wcProjectId
  ? [new WalletConnectSolanaAdapter({ projectId: wcProjectId, network: import.meta.env.VITE_NETWORK })]
  : []

// Global Solana wallet-error sink → dashboard telemetry (issue #80). This is the single emitter of
// adapter ERROR categories, so background/autoConnect failures are captured even when the connect modal
// isn't mounted, and modal-driven attempts aren't double-counted (the modal emits only flow events).
// Note: replaces wallet-adapter's default onError (which console.errored and auto-opened the wallet's
// install page on WalletNotReadyError) — the connect modal now surfaces actionable guidance instead.
const onSolanaWalletError = (error: unknown, adapter?: { name?: string }) => {
  const e = error as { name?: string; message?: string } | null
  emitWalletEvent(categorizeWalletError(error), {
    chain: 'solana',
    wallet: adapter?.name ?? null,
    errorName: e?.name ?? null,
    message: e?.message ? String(e.message) : null,
  })
}

createRoot(document.getElementById('root')!).render(
  // StrictMode intentionally OMITTED: its dev-only double-mount (mount→unmount→remount) orphans the
  // wallet-adapter connect() promise — Phantom-Solana connect spins forever, then autoConnect silently
  // re-establishes the connection on the next page load. StrictMode is a no-op in production builds, so
  // dropping it only affects the dev experience (and fixes the Solana connect hang).
  <QueryClientProvider client={queryClient}>
    <WagmiProvider config={wagmiConfig}>
      <ConnectionProvider endpoint={solanaRpcUrl}>
        <WalletProvider wallets={solanaWallets} autoConnect={true} onError={onSolanaWalletError}>
          <TronWalletProvider>
            <ThemeProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ThemeProvider>
          </TronWalletProvider>
        </WalletProvider>
      </ConnectionProvider>
    </WagmiProvider>
  </QueryClientProvider>,
)
